import React, { Component } from "react";
import {
    Menu,
    Box,
    Radio,
    FormControlLabel,
    Typography,
    IconButton,
    Button,
    Checkbox,
    Switch,
} from "@mui/material";

import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import MicIcon from '@mui/icons-material/Mic';
import TestConsultationPopup from "./TestConsultationPopup";

import { withTranslation } from 'react-i18next';
import { withStyles } from '@mui/styles';
import { playNotificationSound } from "../../config/notification-handler";
import { toast } from 'react-toastify';

const styles = theme => ({
    // write your styles here...
})

class ConsultSettingsPopup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showPlayButtonIcon: true,
            showHeadsetTestButtonIcon: true,
            openTestConsultationPopup: false,
            loader: false,
            audioInputs: [],
            audioOutputs: [],
            videoInputs: [],
            selectedConsultRingingDevice: localStorage.getItem("selectedConsultRingingDevice") || "",
            selectedConsultCallingDevice: localStorage.getItem("selectedConsultCallingDevice") || "",
            selectedConsultMicInput: localStorage.getItem("selectedConsultMicInput") || "",
            selectedConsultCamera: localStorage.getItem("selectedConsultCamera") || "",
            // Audio testing states
            isRecording: false,
            isPlayingRecording: false,
            recordedAudioBlob: null,
            recordedAudioUrl: null,
            isListeningToDevice: false,
            // Refs for audio handling
            mediaRecorder: null,
            audioStream: null,
            audioContext: null,
            audioSource: null,
            audioDestination: null,
            playbackAudio: null,
            listenAudioContext: null,
            listenAudioSource: null,
            listenAudioDestination: null,
            listenGainNode: null
        };
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.loadDevices();
        }
    }

    componentDidMount() {
        this.loadDevices();

        if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
            this.deviceChangeListener = () => {
                this.loadDevices();
            };
            navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeListener);
        }
    }

    componentWillUnmount() {
        if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener && this.deviceChangeListener) {
            navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeListener);
        }
        // Cleanup audio resources
        this.stopRecording();
        this.stopPlayback();
        this.stopListeningToDevice();
    }

    loadDevices = async () => {
        // Check if we're in a secure context (HTTPS or localhost)
        const isSecureContext = window.isSecureContext || 
                               window.location.protocol === 'https:' || 
                               window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1';
        
        if (!isSecureContext) {
            console.warn('Media device access requires HTTPS. Cannot load devices.');
            this.setState({
                audioOutputs: [],
                audioInputs: [],
                videoInputs: [],
                selectedConsultRingingDevice: "",
                selectedConsultCallingDevice: "",
                selectedConsultMicInput: "",
                selectedConsultCamera: ""
            });
            return;
        }
        
        // Check if mediaDevices API is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.warn('getUserMedia is not supported in this browser');
            this.setState({
                audioOutputs: [],
                audioInputs: [],
                videoInputs: [],
                selectedConsultRingingDevice: "",
                selectedConsultCallingDevice: "",
                selectedConsultMicInput: "",
                selectedConsultCamera: ""
            });
            return;
        }
        
        try {
            // Request permission to enumerate devices (required for device labels)
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const devices = await navigator.mediaDevices.enumerateDevices();
            // Stop all tracks immediately to release the microphone
            stream.getTracks().forEach(track => track.stop());
            const audioOutputs = devices.filter(device => device.kind === 'audiooutput' && device.deviceId !== 'communications' && device.deviceId !== 'default');
            const audioInputs = devices.filter((d) => d.kind === "audioinput");
            const videoInputs = devices.filter((d) => d.kind === "videoinput");
            
            // Helper to resolve device: prefer "preferred" if available, then "selected", then first available
            const resolveDevice = (preferredKey, selectedKey, deviceList) => {
                const preferredDevice = localStorage.getItem(preferredKey);
                const selectedDevice = localStorage.getItem(selectedKey);
                const defaultDevice = deviceList.length ? deviceList[0].deviceId : "";
                
                // First priority: preferred device (if it's available)
                if (preferredDevice && deviceList.some(d => d.deviceId === preferredDevice)) {
                    // Preferred device is available - restore it
                    if (selectedDevice !== preferredDevice) {
                        localStorage.setItem(selectedKey, preferredDevice);
                        console.log(`🔄 [Device Restored] Preferred device plugged back in, restored: ${preferredDevice}`);
                    }
                    return preferredDevice;
                }
                
                // Second priority: currently selected device (if still valid)
                if (selectedDevice && deviceList.some(d => d.deviceId === selectedDevice)) {
                    return selectedDevice;
                }
                
                // Fallback: first available device
                if (defaultDevice) {
                    localStorage.setItem(selectedKey, defaultDevice);
                    console.log(`🔊 [Device Fallback] Device unavailable, switched to: ${defaultDevice}`);
                }
                return defaultDevice;
            };
            
            const validRingingDevice = resolveDevice("preferredConsultRingingDevice", "selectedConsultRingingDevice", audioOutputs);
            const validCallingDevice = resolveDevice("preferredConsultCallingDevice", "selectedConsultCallingDevice", audioOutputs);
            const validMicInput = resolveDevice("preferredConsultMicInput", "selectedConsultMicInput", audioInputs);
            const validCamera = resolveDevice("preferredConsultCamera", "selectedConsultCamera", videoInputs);
            
            this.setState({
                audioOutputs,
                audioInputs,
                videoInputs,
                selectedConsultRingingDevice: validRingingDevice,
                selectedConsultCallingDevice: validCallingDevice,
                selectedConsultMicInput: validMicInput,
                selectedConsultCamera: validCamera
            });
        } catch (err) {
            console.error("Error loading devices:", err);
            // If it's a secure context error, log a helpful message
            if (err.message && (err.message.includes('secure context') || err.message.includes('HTTPS'))) {
                console.warn('Media device access requires HTTPS connection');
            }
            // Set empty state on error
            this.setState({
                audioOutputs: [],
                audioInputs: [],
                videoInputs: [],
                selectedConsultRingingDevice: "",
                selectedConsultCallingDevice: "",
                selectedConsultMicInput: "",
                selectedConsultCamera: ""
            });
        }
    };

    handleSelection = async (key, value) => {
        const { audioInputs, audioOutputs, videoInputs, isListeningToDevice, listenAudioContext } = this.state;
        const { isLiveCall, onDeviceSwitch } = this.props;
        let deviceLabel = "Unknown Device";
        let prevDeviceLabel = "unknown";

        if (key === "selectedConsultRingingDevice" || key === "selectedConsultCallingDevice") {
            const device = audioOutputs.find(d => d.deviceId === value);
            deviceLabel = device ? device.label : deviceLabel;
            // Also find previous device label
            const prevDeviceId = this.state[key];
            const prevDevice = audioOutputs.find(d => d.deviceId === prevDeviceId);
            prevDeviceLabel = prevDevice ? prevDevice.label : prevDeviceLabel;
            
            // If listening is active and calling device changes, update output device
            if (key === "selectedConsultCallingDevice" && isListeningToDevice && listenAudioContext) {
                if (listenAudioContext instanceof HTMLAudioElement && typeof listenAudioContext.setSinkId === 'function') {
                    try {
                        await listenAudioContext.setSinkId(value);
                    } catch (err) {
                        console.warn('Failed to update audio output device for listen:', err);
                    }
                }
            }
        } else if (key === "selectedConsultMicInput") {
            const device = audioInputs.find(d => d.deviceId === value);
            deviceLabel = device ? device.label : deviceLabel;

            const prevDeviceId = this.state[key];
            const prevDevice = audioInputs.find(d => d.deviceId === prevDeviceId);
            prevDeviceLabel = prevDevice ? prevDevice.label : prevDeviceLabel;
            
            // If listening is active and microphone changes, restart listening with new device
            if (isListeningToDevice) {
                this.stopListeningToDevice();
                // Restart listening with new device after a brief delay
                setTimeout(() => {
                    this.setState({ isListeningToDevice: true }, () => {
                        this.startListeningToDevice();
                    });
                }, 100);
            }
        } else if (key === "selectedConsultCamera") {
            const device = videoInputs.find(d => d.deviceId === value);
            deviceLabel = device ? device.label : deviceLabel;

            const prevDeviceId = this.state[key];
            const prevDevice = videoInputs.find(d => d.deviceId === prevDeviceId);
            prevDeviceLabel = prevDevice ? prevDevice.label : prevDeviceLabel;
        }

        this.setState({ [key]: value });
        localStorage.setItem(key, value);
        
        // Also save as preferred device (user's manual selection)
        const preferredKey = key.replace("selected", "preferred");
        localStorage.setItem(preferredKey, value);

        if (this.props.handleDeviceSelectionChange) {
            this.props.handleDeviceSelectionChange(key, value, deviceLabel, prevDeviceLabel);
        }

        // If in live call mode, trigger immediate device switch
        if (isLiveCall && onDeviceSwitch) {
            let deviceType = null;
            if (key === "selectedConsultCallingDevice") {
                deviceType = "speaker";
            } else if (key === "selectedConsultMicInput") {
                deviceType = "microphone";
            } else if (key === "selectedConsultCamera") {
                deviceType = "camera";
            }
            // Note: ringing device doesn't need live switching as it's only used for incoming calls
            if (deviceType) {
                onDeviceSwitch(deviceType, value);
            }
        }
    };

    handleAudioOnlyToggle = async (event) => {
        const isChecked = event.target.checked;
        this.props.handleIsAudioOnlyChange && this.props.handleIsAudioOnlyChange(isChecked);

        const user = JSON.parse(sessionStorage.getItem("user"));
        if (!user) return;

        try {
            await fetch(process.env.REACT_APP_BASEURL + 'userconsultupdateaudioonly', {
                method: 'POST',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + user.signInUserSession.idToken.jwtToken,
                },
                body: JSON.stringify({
                    user_id: user.username,
                    is_audio_only: isChecked
                }),
            });

            console.log(`Audio-only setting ${isChecked ? "enabled" : "disabled"} successfully.`);
        } catch (err) {
            console.error("❌ Failed to update audio-only setting:", err);
            toast.error("An error occurred while updating audio-only setting status.");
        }
    };

    renderDeviceSection(title, devices, selectedKey, showPlayButton = false, testButtonType = null) {
        const { isRecording, isPlayingRecording } = this.state;
        
        return (
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography fontWeight="bold" fontSize={16}>
                        {title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        {showPlayButton && (
                            <IconButton
                                size="xs"
                                onClick={() => {
                                    const isPlaying = !this.state.showPlayButtonIcon;
                                    this.setState({ showPlayButtonIcon: isPlaying });
                                    playNotificationSound(isPlaying, this.state.selectedConsultRingingDevice);
                                }}
                                sx={{
                                    backgroundColor: '#00a7e0',
                                    color: 'white',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: '#008ec1' },
                                    borderRadius: 1.5,
                                    '& .MuiSvgIcon-root': {
                                        width: '0.8em',
                                        height: '0.8em'
                                    }
                                }}
                            >
                                {this.state.showPlayButtonIcon ? (
                                    <PlayArrowIcon fontSize="small" />
                                ) : (
                                    <PauseIcon fontSize="small" />
                                )}
                            </IconButton>
                        )}
                        {testButtonType === 'calling' && (
                            <IconButton
                                size="xs"
                                onClick={this.handleTestCallingDevice}
                                sx={{
                                    backgroundColor: '#00a7e0',
                                    color: 'white',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: '#008ec1' },
                                    borderRadius: 1.5,
                                    '& .MuiSvgIcon-root': {
                                        width: '0.8em',
                                        height: '0.8em'
                                    }
                                }}
                                title="Test headset audio"
                            >
                                {this.state.showHeadsetTestButtonIcon ? (
                                    <PlayArrowIcon fontSize="small" />
                                ) : (
                                    <PauseIcon fontSize="small" />
                                )}
                            </IconButton>
                        )}
                        {testButtonType === 'microphone' && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {!isRecording && !isPlayingRecording && (
                                    <React.Fragment>
                                        <IconButton
                                            size="xs"
                                            onClick={this.handleStartRecording}
                                            sx={{
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                padding: '4px',
                                                '&:hover': { backgroundColor: '#c82333' },
                                                borderRadius: 1.5,
                                                '& .MuiSvgIcon-root': {
                                                    width: '0.8em',
                                                    height: '0.8em'
                                                }
                                            }}
                                            title="Record audio"
                                        >
                                            <MicIcon fontSize="small" />
                                        </IconButton>
                                        {this.state.recordedAudioUrl && (
                                            <IconButton
                                                size="xs"
                                                onClick={this.handlePlayRecording}
                                                sx={{
                                                    backgroundColor: '#28a745',
                                                    color: 'white',
                                                    padding: '4px',
                                                    '&:hover': { backgroundColor: '#218838' },
                                                    borderRadius: 1.5,
                                                    '& .MuiSvgIcon-root': {
                                                        width: '0.8em',
                                                        height: '0.8em'
                                                    }
                                                }}
                                                title="Play recorded audio"
                                            >
                                                <PlayArrowIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </React.Fragment>
                                )}
                                {isRecording && (
                                    <IconButton
                                        size="xs"
                                        onClick={this.stopRecording}
                                        sx={{
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            padding: '4px',
                                            '&:hover': { backgroundColor: '#c82333' },
                                            borderRadius: 1.5,
                                            '& .MuiSvgIcon-root': {
                                                width: '0.8em',
                                                height: '0.8em'
                                            }
                                        }}
                                        title="Stop recording"
                                    >
                                        <StopIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {isPlayingRecording && (
                                    <IconButton
                                        size="xs"
                                        onClick={this.stopPlayback}
                                        sx={{
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            padding: '4px',
                                            '&:hover': { backgroundColor: '#218838' },
                                            borderRadius: 1.5,
                                            '& .MuiSvgIcon-root': {
                                                width: '0.8em',
                                                height: '0.8em'
                                            }
                                        }}
                                        title="Stop playback"
                                    >
                                        <StopIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
                {devices.map((device) => (
                    <FormControlLabel
                        key={device.deviceId}
                        control={
                            <Radio
                                size="small"
                                checked={this.state[selectedKey] === device.deviceId}
                                onChange={() => this.handleSelection(selectedKey, device.deviceId)}
                            />
                        }
                        label={<Typography fontSize={14}>{device.label || "Unknown Device"}</Typography>}
                        sx={{ ml: 1 }}
                    />
                ))}
            </Box>
        );
    }


    handleShowTestConsultation = (value) => {
        console.log(`🖱️ [Event] Home.handleConsultDiagnostics: Consult diagnostics value changed`, { value });
        this.setState({ openTestConsultationPopup: value, loader: value });
        console.log(`🔄 [State] Home.handleConsultDiagnostics: Updated consult diagnostics state`);
    };

    handleCloseTestConsultationPopupLoader = () => {
        this.setState({ loader: false });
    };

    // Test calling device (headset) audio output - toggle play/pause
    handleTestCallingDevice = async () => {
        const { selectedConsultCallingDevice, showHeadsetTestButtonIcon } = this.state;
        if (!selectedConsultCallingDevice) {
            toast.warning("Please select a headset device first");
            return;
        }

        const isPlaying = !showHeadsetTestButtonIcon;
        this.setState({ showHeadsetTestButtonIcon: isPlaying });
        playNotificationSound(isPlaying, selectedConsultCallingDevice);
    };

    // Start recording microphone input
    handleStartRecording = async () => {
        const { selectedConsultMicInput } = this.state;
        if (!selectedConsultMicInput) {
            toast.warning("Please select a microphone first");
            return;
        }

        try {
            const constraints = {
                audio: {
                    deviceId: { exact: selectedConsultMicInput }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            this.setState({ audioStream: stream });

            const mediaRecorder = new MediaRecorder(stream);
            const chunks = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: 'audio/webm' });
                const url = URL.createObjectURL(blob);
                this.setState({
                    recordedAudioBlob: blob,
                    recordedAudioUrl: url,
                    isRecording: false
                });
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            this.setState({
                mediaRecorder,
                isRecording: true
            });

            toast.info("Recording started... Speak into your microphone");
        } catch (err) {
            console.error("Error starting recording:", err);
            toast.error("Failed to start recording. Please check microphone permissions.");
            this.setState({ isRecording: false });
        }
    };

    // Stop recording
    stopRecording = () => {
        const { mediaRecorder, audioStream } = this.state;
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
        }
        this.setState({
            isRecording: false,
            mediaRecorder: null,
            audioStream: null
        });
    };

    // Play recorded audio
    handlePlayRecording = async () => {
        const { recordedAudioUrl, selectedConsultCallingDevice } = this.state;
        if (!recordedAudioUrl) {
            toast.warning("No recording available. Please record first.");
            return;
        }

        try {
            // Stop any existing playback
            this.stopPlayback();

            const audio = new Audio(recordedAudioUrl);
            
            // Set output device if supported
            if (typeof audio.setSinkId === 'function' && selectedConsultCallingDevice) {
                try {
                    await audio.setSinkId(selectedConsultCallingDevice);
                } catch (err) {
                    console.warn('Failed to set audio output device:', err);
                }
            }

            audio.onended = () => {
                this.setState({ isPlayingRecording: false, playbackAudio: null });
            };

            audio.onerror = (err) => {
                console.error("Error playing recording:", err);
                toast.error("Failed to play recording");
                this.setState({ isPlayingRecording: false, playbackAudio: null });
            };

            await audio.play();
            this.setState({
                isPlayingRecording: true,
                playbackAudio: audio
            });
        } catch (err) {
            console.error("Error playing recording:", err);
            toast.error("Failed to play recording");
            this.setState({ isPlayingRecording: false });
        }
    };

    // Stop playback
    stopPlayback = () => {
        const { playbackAudio } = this.state;
        if (playbackAudio) {
            playbackAudio.pause();
            playbackAudio.currentTime = 0;
        }
        this.setState({
            isPlayingRecording: false,
            playbackAudio: null
        });
    };

    // Toggle "Listen to device" - plays microphone input through headset output
    handleToggleListenToDevice = async (event) => {
        const isEnabled = event.target.checked;
        
        if (isEnabled) {
            await this.startListeningToDevice();
        } else {
            this.stopListeningToDevice();
        }
    };

    // Start listening to device (microphone input -> headset output)
    startListeningToDevice = async () => {
        const { selectedConsultMicInput, selectedConsultCallingDevice } = this.state;
        
        if (!selectedConsultMicInput) {
            toast.warning("Please select a microphone first");
            this.setState({ isListeningToDevice: false });
            return;
        }

        if (!selectedConsultCallingDevice) {
            toast.warning("Please select a headset device first");
            this.setState({ isListeningToDevice: false });
            return;
        }

        try {
            const constraints = {
                audio: {
                    deviceId: { exact: selectedConsultMicInput }
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            
            // Use Audio element with MediaStream for playback
            // This allows us to use setSinkId to route to the selected output device
            const audioElement = document.createElement('audio');
            audioElement.autoplay = true;
            audioElement.srcObject = stream;
            audioElement.volume = 0.5; // 50% volume to prevent feedback
            audioElement.style.display = 'none';
            document.body.appendChild(audioElement);
            
            // Set output device if supported
            if (typeof audioElement.setSinkId === 'function' && selectedConsultCallingDevice) {
                try {
                    await audioElement.setSinkId(selectedConsultCallingDevice);
                } catch (err) {
                    console.warn('Failed to set audio output device for listen:', err);
                }
            }

            this.setState({
                isListeningToDevice: true,
                listenAudioContext: audioElement,
                audioStream: stream
            });

            toast.info("Listening to microphone - you should hear your voice through the headset");
        } catch (err) {
            console.error("Error starting listen to device:", err);
            toast.error("Failed to start listening. Please check microphone permissions.");
            this.setState({ isListeningToDevice: false });
        }
    };

    // Stop listening to device
    stopListeningToDevice = () => {
        const { listenAudioContext, audioStream } = this.state;
        
        if (listenAudioContext && listenAudioContext instanceof HTMLAudioElement) {
            listenAudioContext.pause();
            listenAudioContext.srcObject = null;
            if (listenAudioContext.parentNode) {
                listenAudioContext.parentNode.removeChild(listenAudioContext);
            }
        }
        
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
        }

        this.setState({
            isListeningToDevice: false,
            listenAudioContext: null,
            audioStream: null
        });
    };

    // Handle modal close - cleanup all active audio resources
    handleModalClose = () => {
        // Stop all active audio operations when popup closes
        if (this.state.isRecording) {
            this.stopRecording();
        }
        if (this.state.isPlayingRecording) {
            this.stopPlayback();
        }
        if (this.state.isListeningToDevice) {
            this.stopListeningToDevice();
        }
        // Call the original onClose handler
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    render() {
        const {
            anchorEl,
            open,
            t,
        } = this.props;

        const {
            audioOutputs,
            audioInputs,
            videoInputs,
        } = this.state;

        const currentLang = localStorage.getItem('i18nextLng');

        return (
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={this.handleModalClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        width: 420,
                        mt: 6,
                        p: '12px',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        rowGap: "16px",
                    }}
                >
                    {this.renderDeviceSection(
                        "Select a device for ring notifications",
                        audioOutputs,
                        "selectedConsultRingingDevice",
                        true
                    )}
                    {this.renderDeviceSection(
                        "Select a headset for patient's audio",
                        audioOutputs,
                        "selectedConsultCallingDevice",
                        false,
                        'calling'
                    )}
                    <Box>
                        {this.renderDeviceSection(
                            "Select a microphone for audio consultation",
                            audioInputs,
                            "selectedConsultMicInput",
                            false,
                            'microphone'
                        )}
                        <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={this.state.isListeningToDevice}
                                        onChange={this.handleToggleListenToDevice}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#00a7e0',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#00a7e0',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography fontSize={14}>
                                        Listen to device
                                    </Typography>
                                }
                                sx={{ m: 0 }}
                            />
                            {this.state.isListeningToDevice && (
                                <Typography fontSize={12} color="text.secondary">
                                    Microphone input playing through headset
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {this.renderDeviceSection(
                        "Select a camera for video consultation",
                        videoInputs,
                        "selectedConsultCamera",
                        false,
                        null
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} width={"100%"} columnGap={'8px'}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={this.props.isAudioOnlyConsult}
                                    onChange={this.handleAudioOnlyToggle}
                                    size="small"
                                    sx={{
                                        padding: '0px',
                                        paddingRight: '6px',
                                    }}
                                />
                            }
                            label={<Typography fontSize={14}>Enable Audio-only Consult</Typography>}
                            sx={{ m: 0 }}
                        />
                        {currentLang === "en" && (
                            <Button
                                variant="contained"
                                onClick={() => this.handleShowTestConsultation(true)}
                                sx={{
                                    backgroundColor: '#00a7e0',
                                    color: 'white',
                                    padding: '6px 8px',
                                    fontSize: '14px',
                                    '&:hover': { backgroundColor: '#008ec1' }
                                }}
                            >
                                {t("Navbar.TestConsult")}
                            </Button>
                        )}
                    </Box>
                </Box>

                <TestConsultationPopup
                    openTestConsultationPopup={!window.location.host.includes(".ca") &&
                        this.state.openTestConsultationPopup}
                    handleShowTestConsultation={this.handleShowTestConsultation}
                    loader={this.state.loader}
                    handleCloseTestConsultationPopupLoader={this.handleCloseTestConsultationPopupLoader} />
            </Menu>
        );
    }
}

export default withStyles(styles, { withTheme: true })(withTranslation()(ConsultSettingsPopup));



// WEBPACK FOOTER //
// ./src/components/common/ConsultSettingsPopup.js